import { parseAbsoluteToLocal } from "@internationalized/date"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { GatedLookupSessionsPOSTRequest } from "ipctg-inflow-types/InFlowGatedAPI"
import { useEffect } from "react"
import { GridList, GridListItem } from "react-aria-components"
import { Controller, FormProvider, useForm, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, ButtonLoader } from "@/globalComponents/atoms/Button"
import Card from "@/globalComponents/atoms/Card"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Icon from "@/globalComponents/atoms/Icon"
import Label from "@/globalComponents/atoms/Label"
import Separator from "@/globalComponents/atoms/Separator"
import Text from "@/globalComponents/atoms/Text"
import TermsAndConditions from "@/globalComponents/molecules/TermsAndConditions"
import TextField from "@/globalComponents/molecules/TextField"
import { GatedLookupSessionsError } from "@/utilities/expectedErrors"
import { requiredRule } from "@/utilities/form"
import { fetchWelcomeToken } from "@/utilities/hooks/queries/shared"
import { meQueryOptions } from "@/utilities/hooks/queries/user"
import useToggle from "@/utilities/hooks/useToggle"
import { normalizeLicensePlate } from "@/utilities/normalizers"
import { GatedLookupSessionsResponse } from "@/utilities/types/rsp"

import NoPlateBasedSessionModal from "../-components/NoPlateBasedSessionModal"

export const Route = createFileRoute("/qr/$qrLookupID/gated/$pamID/look-up/")({
  beforeLoad: async ({ context, params }) => {
    await fetchWelcomeToken({ qrLookupID: params.qrLookupID })
    context.queryClient.clear()
  },
  component: RouteComponent,
})

interface RegistrationFormSchema {
  plate: string
}

function RouteComponent() {
  const navigate = Route.useNavigate()
  const { qrLookupID } = Route.useParams()
  const { t } = useTranslation()
  const [showModal, toggleModal] = useToggle(false)
  const { data: me } = useSuspenseQuery(meQueryOptions())
  const form = useForm<RegistrationFormSchema>({
    defaultValues: {
      plate: "",
    },
    mode: "onBlur",
  })
  const plate = useWatch({
    control: form.control,
    name: "plate",
    defaultValue: "",
  })

  const { mutate, isPending, reset, variables } = useMutation({
    mutationFn,
    onSuccess,
    throwOnError: true,
  })

  useEffect(() => {
    form.reset(undefined, { keepValues: true })
  }, [form.formState.isSubmitSuccessful])

  return (
    <>
      <Heading className="center" level={3} as={1}>
        {t("inlane.lookup.header")}
      </Heading>
      <Card>
        <Heading level={1} as={2}>
          {t("inlane.lookup.subhead")}
        </Heading>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <fieldset disabled={isPending}>
              {!!me?.inflowUser.savedPlates.length && (
                <>
                  <div className="field">
                    <Label id="label-saved-plates">
                      {t("register.vehicle.savedVehiclesLabel")}
                    </Label>
                    <GridList
                      aria-label={t("register.vehicle.savedVehiclesLabel")}
                      aria-labelledby="label-saved-plates"
                      className="field--input"
                      selectionBehavior="replace"
                      disallowEmptySelection
                      dependencies={[
                        me.inflowUser.savedPlates,
                        variables,
                        isPending,
                      ]}
                      disabledKeys={
                        isPending
                          ? me.inflowUser.savedPlates.map((v) => v.plate)
                          : undefined
                      }
                      // disabledKeys={isPending ? "all" : undefined}
                      items={me.inflowUser.savedPlates.sort((a, b) =>
                        parseAbsoluteToLocal(a.lastUsed).compare(
                          parseAbsoluteToLocal(b.lastUsed)
                        )
                      )}
                    >
                      {(item) => (
                        <GridListItem
                          id={`${item.lastUsed}${item.nickname}${item.plate}`}
                          key={`${item.lastUsed}${item.nickname}${item.plate}`}
                          className="saved-vehicle-input"
                          onAction={() => {
                            mutate({
                              plate: item.plate,
                              phone: null,
                              method: "plate",
                            })
                          }}
                        >
                          <Icon name="car" />
                          <Text as="span" className="semi-bold start">
                            {item.nickname ? (
                              <>
                                {item.nickname}&nbsp;
                                {
                                  <Text as="span" className="regular">
                                    ({item.plate})
                                  </Text>
                                }
                              </>
                            ) : (
                              item.plate
                            )}
                          </Text>

                          {isPending && item.plate === variables.plate ? (
                            <ButtonLoader
                              style={{
                                display: "block",
                                position: "initial",
                                transform: "initial",
                              }}
                              size={20}
                            />
                          ) : (
                            <Icon name="chevronRight" />
                          )}
                        </GridListItem>
                      )}
                    </GridList>
                  </div>
                  <div className="horizontal-separator-with-text">
                    <Separator orientation="horizontal" />
                    <Text level={3} className="bold">
                      {t("register.vehicle.orManuallyEnter")}
                    </Text>
                    <Separator orientation="horizontal" />
                  </div>
                </>
              )}
              <Controller<RegistrationFormSchema>
                name="plate"
                rules={{ required: requiredRule(t("errors.common.required")) }}
                render={({
                  field: { ref, onChange, ...rest },
                  fieldState: { error, invalid },
                }) => (
                  <TextField
                    {...rest}
                    onChange={(v) => onChange(normalizeLicensePlate(v))}
                    isInvalid={invalid}
                  >
                    <Label>{t("inlane.lookup.fieldLabel")}</Label>
                    <TextField.Input innerRef={ref} />
                    <FieldError>{error?.message}</FieldError>
                  </TextField>
                )}
              />
              <Button
                type="submit"
                hue="secondary"
                variant="default"
                isPending={isPending}
              >
                {t("common.continue")}
                <Icon name="arrowRight" slot="right" />
              </Button>
            </fieldset>
          </form>
        </FormProvider>
      </Card>
      <Text level={3} className="center">
        <TermsAndConditions qrLookupID={qrLookupID} />
      </Text>
      <NoPlateBasedSessionModal
        isOpen={showModal}
        onOpenChange={toggleModal}
        plate={plate}
        resetMutation={reset}
        onConfirm={() =>
          navigate({
            to: "/qr/$qrLookupID/gated/$pamID/look-up/challenge",
            search: { plate },
            mask: {
              to: "/qr/$qrLookupID/gated/$pamID/look-up",
            },
          })
        }
      />
    </>
  )

  function onSubmit(data: RegistrationFormSchema) {
    mutate({
      plate: data.plate,
      phone: null,
      method: "plate",
    })
  }

  function mutationFn(request: GatedLookupSessionsPOSTRequest) {
    return api.lookupSessions.POST(request)
  }

  function onSuccess(rsp: GatedLookupSessionsResponse) {
    if ("failReason" in rsp) {
      throw new GatedLookupSessionsError(rsp.failReason)
    }

    const session = rsp.sessions.length && rsp.sessions[0]
    if (session) {
      const { sessionID } = session
      navigate({
        to: "/qr/$qrLookupID/gated/$pamID/$sessionID/cart",
        params: { sessionID },
        search: { from: Route.id },
      })
    } else {
      toggleModal()
    }
  }
}
